import React from "react";
import { useTheme } from "react-jss";
import { useSelector } from "react-redux";
import useStyles from "./DrillDownRenderer.styles";
import { VariableModel } from "../../../models/VariableModel";
import { StoreState } from "../../../models/states/StoreState";
import { translate } from "../../../models/utilities/translationUtilities";
import { getRendererProps, RendererProps } from "../BaseRenderer";
import { removeHtmlTags, isNullOrEmpty } from "../../../utils";
import Select from "../../select/Select";
import { MentionModel } from "../../../models/MentionModel";
import { ChoiceModel } from "../../../models/ChoiceModel";
import ChoiceOpenEndRenderer from "./ChoiceOpenEndRenderer";
import { size } from "../../../models/utilities/questionUtilities";
import { getSettingString, getSettingNumber } from "../../../models/utilities/settingUtilities";
import useRecall from "../../../hooks/useRecall";
import { usePickMentions } from "../../../hooks/useMentions";

interface VariableProps extends RendererProps {
    variable: VariableModel;
    parentVariable: VariableModel;
    mentions: { [variableName: string]: MentionModel[] };
    onVariableChanged(variableName: string, value: string[]): void;
    openEndValue?: { [key: string]: string };
    getFirstOpenEndedChoice(variable: VariableModel): ChoiceModel;
    onOpenEndChange(variable: VariableModel, choice: ChoiceModel, text: string): void;
    onOpenEndBlur(variable: VariableModel, choice: ChoiceModel): void;
}

function DrillDownVariable(props: VariableProps) {
    const {
        question,
        variable,
        parentVariable,
        mentions,
        onVariableChanged,
        openEndValue,
        getFirstOpenEndedChoice,
        onOpenEndChange,
        onOpenEndBlur,
    } = props;

    const theme = useTheme();
    const classes = useStyles({ ...props, theme });
    const language = useSelector((state: StoreState) => state.interview.language);
    const surveyTheme = useSelector((state: StoreState) => state.interview.theme);

    const openEndChoice = getFirstOpenEndedChoice(variable);

    const min = variable.settings.MinRequiredAnswers;
    const max = variable.settings.MaxAllowedAnswers;
    const varMentions = mentions[variable.name];
    const parentVarMentions = parentVariable ? mentions[parentVariable.name] : undefined;
    const isMultiple = min > 1 || max > 1;

    const availableChoices = React.useMemo(() => {
        if (!parentVariable) {
            return variable.choiceList;
        }

        const parentMentionValues = parentVarMentions ? parentVarMentions.map((m) => m.value) : [];
        return variable.choiceList.filter(
            (choice) =>
                isNullOrEmpty(choice.parentValue) ||
                parentMentionValues.includes(choice.parentValue),
        );
    }, [parentVariable, parentVarMentions, variable]);

    const availableOptions = React.useMemo(
        () =>
            availableChoices.map((choice) => ({
                id: choice.elementId,
                label: translate(choice, "TEXT", language),
                value: choice.value,
            })),
        [availableChoices, language],
    );

    const emptyAnswerOption = React.useMemo(
        () => ({
            id: `${variable.elementId}_EMPTY`,
            label: removeHtmlTags(translate(surveyTheme, "DropdownEmptyAnswer", language)),
            value: "",
        }),
        [variable, language, surveyTheme],
    );

    const options = React.useMemo(() => [emptyAnswerOption, ...availableOptions], [
        emptyAnswerOption,
        availableOptions,
    ]);

    const variableText = useRecall(translate(variable, "Text", language));

    return (
        <div key={variable.elementId} className={classes.variable}>
            <div
                id={`${variable.elementId}_LABEL`}
                className={classes.label}
                dangerouslySetInnerHTML={{ __html: variableText }}
            />
            <Select
                id={`${variable.elementId}_INPUT`}
                labelId={`${variable.elementId}_LABEL`}
                value={varMentions ? varMentions.map((m) => m.value || "") : []}
                multiple={isMultiple}
                onChange={(value) => onVariableChanged(variable.name, value)}
                options={options}
                size={size(question)}
            />
            {openEndChoice && (
                <ChoiceOpenEndRenderer
                    {...getRendererProps(props)}
                    choice={openEndChoice}
                    isSelected
                    name={openEndChoice.elementId}
                    label={removeHtmlTags(translate(openEndChoice, "TEXT", language))}
                    value={openEndValue[variable.name]}
                    onOpenEndBlur={(choice) => onOpenEndBlur(variable, choice)}
                    onOpenEndChange={(choice, text) => onOpenEndChange(variable, choice, text)}
                    size={getSettingString(openEndChoice, "OpenEndSize", "Medium")}
                    lines={getSettingNumber(openEndChoice, "OpenEndLines", 2)}
                />
            )}
        </div>
    );
}

interface Props extends RendererProps {
    variables: VariableModel[];
    onVariableChanged(variableName: string, value: string[]): void;
    openEndValue?: { [key: string]: string };
    getFirstOpenEndedChoice(variable: VariableModel): ChoiceModel;
    onOpenEndChange(variable: VariableModel, choice: ChoiceModel, text: string): void;
    onOpenEndBlur(variable: VariableModel, choice: ChoiceModel): void;
}

export default function DrillDownRenderer(props: Props) {
    const {
        variables,
        onVariableChanged,
        openEndValue,
        getFirstOpenEndedChoice,
        onOpenEndChange,
        onOpenEndBlur,
    } = props;
    const theme = useTheme();
    const classes = useStyles({ ...props, theme });
    const drillDownMentions = usePickMentions(variables.map((v) => v.name));

    return (
        <div className={classes.drillDown}>
            {variables.map((variable) => (
                <DrillDownVariable
                    {...getRendererProps(props)}
                    key={variable.name}
                    variable={variable}
                    parentVariable={variables[variables.indexOf(variable) - 1]}
                    mentions={drillDownMentions}
                    onVariableChanged={onVariableChanged}
                    openEndValue={openEndValue}
                    getFirstOpenEndedChoice={getFirstOpenEndedChoice}
                    onOpenEndChange={onOpenEndChange}
                    onOpenEndBlur={onOpenEndBlur}
                />
            ))}
        </div>
    );
}
